import { SVGProps } from 'react'
import type { AreaProps, DotProps, LineProps, YAxisProps } from 'recharts'

import { FetchCoinPricesBySlugPayload } from '@/services/coin/prices'
import { fiatCurrencyFormat, toFixed } from '@/utils/number'

export enum SettingType {
  TRADING_VIEW = 'tradingView',
  BTC_PRICE = 'btcPrice',
}

export const activeDotProps: Omit<DotProps, 'ref' | 'strokeWidth'> & { strokeWidth: number } = {
  r: 2.5,
  strokeWidth: 4,
  strokeOpacity: 0.1,
  stroke: 'var(--common-white)',
  fill: 'var(--common-white)',
}

export const baseTickProps: SVGProps<SVGTextElement> = {
  fontSize: 12,
  fill: 'var(--content-secondary)',
  fontWeight: 500,
  height: 16,
}

export const baseYaxisProps: Omit<YAxisProps, 'ref'> = {
  tickFormatter: value => (value ? fiatCurrencyFormat(value) : ''),
  padding: {
    top: 15,
    bottom: 15,
  },
  domain: ['dataMin', 'dataMax'],
  axisLine: false,
  tickLine: false,
}

export const baseAnimationProps: Partial<AreaProps & LineProps> = {
  isAnimationActive: true,
  animationEasing: 'linear',
  animationDuration: 500,
}

function getYAxisMinMax(maxPrice: number, minPrice: number, lastPrice: number) {
  if ((maxPrice - minPrice) / maxPrice < 0.0005) {
    return [lastPrice * 0.999, lastPrice * 1.001]
  }
  const diff = maxPrice - minPrice
  const margin = (diff / 0.8 - diff) / 2
  return [Math.max(minPrice - margin, 0), maxPrice + margin]
}

export function getYAxisTicks(data: number[]) {
  const maxPrice = Math.max(...data)
  const minPrice = Math.min(...data)
  if (maxPrice === minPrice) return [maxPrice]
  const [yAxisMin, yAxisMax] = getYAxisMinMax(maxPrice, minPrice, data[data.length - 1])
  const middle = (yAxisMin + yAxisMax) / 2

  return [yAxisMin, (yAxisMin + middle) / 2, middle, (yAxisMax + middle) / 2, yAxisMax]
}

export function formatChangePercent(changePercent: string | number) {
  const num = Number(changePercent)
  if (num === 0) {
    return '0.00%'
  }
  if (num > 0) {
    return `+${toFixed(changePercent)}%`
  }
  return `${toFixed(changePercent)}%`
}

export function getPercentChangeColor(changeValue: number) {
  if (changeValue > 0) {
    return 'var(--content-success)'
  }
  if (changeValue < 0) {
    return 'var(--content-danger)'
  }
  return 'var(--content-tertiary)'
}

export const BTC_SLUG = 'bitcoin'

export const CHART_HEIGHT = 212

export const TIME_RANGE_OPTIONS: {
  value: string
  label: string
  getQueryParams: () => Omit<FetchCoinPricesBySlugPayload, 'slug'>
}[] = [
  {
    value: '1H',
    label: '1H',
    getQueryParams: () => {
      return {
        interval: 'h',
      }
    },
  },
  {
    value: '24H',
    label: '24H',
    getQueryParams: () => {
      return {
        interval: 'd',
      }
    },
  },
  {
    value: '1W',
    label: '1W',
    getQueryParams: () => {
      return {
        interval: 'w',
      }
    },
  },
  {
    value: '1M',
    label: '1M',
    getQueryParams: () => {
      return {
        interval: 'm',
      }
    },
  },
  {
    value: '3M',
    label: '3M',
    getQueryParams: () => {
      return {
        interval: '3m',
      }
    },
  },
  {
    value: '6M',
    label: '6M',
    getQueryParams: () => {
      return {
        interval: '6m',
      }
    },
  },
  {
    value: '1Y',
    label: '1Y',
    getQueryParams: () => {
      return {
        interval: 'y',
      }
    },
  },
  {
    value: 'ALL',
    label: 'ALL',
    getQueryParams: () => {
      return {
        interval: 'all',
      }
    },
  },
]
